// ── API Client ──────────────────────────────────────────────
// Sanctum SPA convention (D-05, AUTH-09):
//   • withCredentials → cookies (laravel_session + XSRF-TOKEN) flow on every request.
//   • withXSRFToken → axios reads the XSRF-TOKEN cookie and sends it as X-XSRF-TOKEN.
//   • bootCsrf() must be called once before any state-changing request to populate
//     the XSRF-TOKEN cookie via /sanctum/csrf-cookie.
//
// Existing named exports (api, ApiClientError, isApiError, isAuthError,
// setCsrfToken, getCsrfToken, setOnAuthError) are preserved so the rest of the
// SPA continues to compile. The legacy in-memory CSRF token API is now a no-op
// shim — Sanctum manages the token via cookies — and exists only to keep the
// old call sites quiet until Plan 03 reshapes them.

import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@/types';

export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string = 'API_ERROR',
    public details?: Record<string, string[]>,
    /**
     * The refusal's structured payload, when the endpoint sends one.
     *
     * Added in Phase 14 Plan 14-13. `POST /api/reference-photos` answers a
     * refusal in three dialects at once (Laravel's `message`/`errors`, the
     * SPA's `error.code`/`error.details`, and a top-level
     * `reason_code`/`payload`), and only the middle one survived this
     * interceptor. The third is the one that carries `primary_face_box` and
     * `required_min_edge` — the numbers that turn a multiple-faces rejection
     * into a seeded crop rather than a wall. Dropping it here is why it has to
     * be picked up here.
     *
     * Deliberately `unknown` rather than a union of every endpoint's payload
     * shape: each caller narrows the one shape it asked for, and a shared type
     * would have to be edited by every future endpoint that sends one.
     */
    public payload?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export function isApiError(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError;
}

export function isAuthError(error: unknown): boolean {
  return isApiError(error) && error.status === 401;
}

let onAuthError: (() => void) | null = null;

// Legacy CSRF-token shim — Sanctum manages CSRF via cookies, so these are no-ops.
// Plan 03 removes the call sites entirely.
export function setCsrfToken(_token: string | null): void {
  // intentionally no-op
}
export function getCsrfToken(): string | null {
  return null;
}

export function setOnAuthError(handler: () => void): void {
  onAuthError = handler;
}

const apiClient: AxiosInstance = axios.create({
  baseURL: '/',
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

apiClient.interceptors.response.use(
  (res) => res,
  (err: AxiosError) => {
    if (err.response?.status === 401 && onAuthError) {
      onAuthError();
    }
    const status = err.response?.status ?? 0;
    const body = err.response?.data as
      | {
          error?: { message?: string; code?: string; details?: Record<string, string[]> };
          payload?: unknown;
        }
      | undefined;
    const message = body?.error?.message ?? err.message ?? 'An error occurred';
    const code = body?.error?.code ?? 'API_ERROR';
    const details = body?.error?.details;
    return Promise.reject(new ApiClientError(message, status, code, details, body?.payload));
  },
);

/**
 * Prime the XSRF-TOKEN cookie. Call once at app boot before any state-changing
 * request (POST/PUT/PATCH/DELETE) to /api/* or /web routes.
 */
export async function bootCsrf(): Promise<void> {
  await apiClient.get('/sanctum/csrf-cookie');
}

function unwrap<T>(payload: unknown): T {
  // Laravel responses may be either ApiResponse<T> envelopes or raw bodies.
  if (
    payload !== null &&
    typeof payload === 'object' &&
    'data' in (payload as Record<string, unknown>) &&
    'success' in (payload as Record<string, unknown>)
  ) {
    return (payload as ApiResponse<T>).data;
  }
  return payload as T;
}

export const api = {
  async get<T>(url: string): Promise<T> {
    const res = await apiClient.get(url);
    return unwrap<T>(res.data);
  },
  /**
   * `config` is passed straight through to axios. It exists for
   * `onUploadProgress`, which the reference-photo step needs and which had no
   * way in: a multipart POST of a phone photo on a slow connection is several
   * seconds of nothing otherwise. Optional and third, so no existing call site
   * changes.
   *
   * Note that axios sets `multipart/form-data` with the correct boundary by
   * itself when handed a FormData, so an upload needs no header of its own —
   * setting one by hand is how the boundary goes missing.
   */
  async post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const res = await apiClient.post(url, body, config);
    return unwrap<T>(res.data);
  },
  async put<T>(url: string, body?: unknown): Promise<T> {
    const res = await apiClient.put(url, body);
    return unwrap<T>(res.data);
  },
  async patch<T>(url: string, body?: unknown): Promise<T> {
    const res = await apiClient.patch(url, body);
    return unwrap<T>(res.data);
  },
  async del<T>(url: string): Promise<T> {
    const res = await apiClient.delete(url);
    return unwrap<T>(res.data);
  },
  /**
   * A binary GET that keeps the server's filename.
   *
   * Every other method here throws the response away after unwrapping the body,
   * which is right for JSON and wrong for a download: the one thing a download
   * needs beyond the bytes is what to call the file, and that arrives in
   * `Content-Disposition`. Parsing it here rather than in the component keeps
   * exactly one implementation of that header's grammar.
   *
   * `filename` is null when the server sent none, so the caller decides its own
   * fallback rather than being handed an invented name.
   */
  async getBlob(url: string): Promise<{ blob: Blob; filename: string | null }> {
    const res = await apiClient.get(url, { responseType: 'blob' });
    const disposition = res.headers['content-disposition'] as string | undefined;

    return { blob: res.data as Blob, filename: parseFilename(disposition) };
  },
};

/**
 * Pull the filename out of a `Content-Disposition` header.
 *
 * Handles the quoted form Laravel's `streamDownload` emits and the RFC 5987
 * `filename*=UTF-8''...` form a proxy may rewrite it into. Returns null rather
 * than guessing, because a wrong filename is worse than no filename: the caller
 * knows what kind of thing it asked for and this function does not.
 */
function parseFilename(disposition: string | undefined): string | null {
  if (disposition === undefined) return null;

  const extended = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (extended?.[1] !== undefined) {
    try {
      return decodeURIComponent(extended[1]);
    } catch {
      // A malformed percent-escape is not worth throwing over on a download.
      return null;
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain?.[1]?.trim() ?? null;
}

export default apiClient;
